import { Select, Checkbox, Label } from '@perch-labs/shrike';
import { randomNumber } from '@perch-labs/warbler';
import { inspect } from '@xstate/inspect';
import copyToClipboard from 'copy-to-clipboard';
import { kebabCase, sortBy } from 'lodash';
import RandExp from 'randexp';
import { useMemo, useState } from 'react';
import { AnyState } from 'xstate';
import { Button } from '~/components/button';
import { config as businessConfig } from '~/funnels/business/config';
import { config as communityHousingPartnersConfig } from '~/funnels/community-housing-partners/config';
import { config as communitySolarConfig } from '~/funnels/community-solar/config';
import { config as conferenceConfig } from '~/funnels/conference/config';
import { config as constellationConfig } from '~/funnels/constellation/config';
import { config as dashboardBillpayUpgradeConfig } from '~/funnels/dashboard-billpay-upgrade/config';
import { config as goSolarConfig } from '~/funnels/go-solar/config';
import { config as isolarUcbConfig } from '~/funnels/isolar-ucb/config';
import { config as membershipConfig } from '~/funnels/membership/config';
import { config as minnesotaLmiConfig } from '~/funnels/minnesota-lmi/config';
import { config as nonABPConfig } from '~/funnels/non-abp/config';
import { config as oregonTier1Config } from '~/funnels/oregon-tier-1/config';
import { config as oregonTier1LmiConfig } from '~/funnels/oregon-tier-1-lmi/config';
import { config as oregonTier2Config } from '~/funnels/oregon-tier-2/config';
import { config as oregonTier2LmiConfig } from '~/funnels/oregon-tier-2-lmi/config';
import { PERCH_FUNNEL_ID, config as perchEnergyConfig } from '~/funnels/perch-energy/config';
import { config as powerSetterConfig } from '~/funnels/powersetter/config';
import { config as resumingConfig } from '~/funnels/resuming/config';
import { config as solarLandscapeConfig } from '~/funnels/solar-landscape/config';
import { config as telesalesConfig } from '~/funnels/telesales/config';
import { config as telesalesNoPaymentConfig } from '~/funnels/telesales-no-payment/config';
import { config as waitlListConfig } from '~/funnels/waitlist/config';
import { config as welcomeBackConfig } from '~/funnels/welcome-back/config';
import { useStoreToggle } from '~/hooks';
import { IS_PRODUCTION, UTILITY_IDS } from '~/utils/constants';
import { FunnelConfig } from '~/utils/funnels';
import { store } from '~/utils/store';
import { TEST_DATA } from '~/utils/test-data';
import { AutofillUtilities } from './autofill-utilities.component';
import { CredentialsStatus } from './credentials-status.component';
import { Additional, Container, Controls, Summary } from './dev-tools.style';
import { generateAccountNumber, setValueByName } from './dev-tools.utils';
import { PerchEnergyDevInfo } from './perch-energy-dev-info.component';
import { ResumingButtons } from './resuming-buttons.component';
// @ts-expect-error TS doesn't like direct JSON imports - need to adjust the tsconfig
import utilityData from './utility-dropdown-data.json';

if (!IS_PRODUCTION && store.get('SHOW_DIAGRAMS')) inspect({ iframe: false });

type DevToolsButtonProps = {
  currentState?: AnyState;
  funnelId: string;
  next: (data: any) => void;
};

export const DevToolsButton = ({ currentState, funnelId, next }: DevToolsButtonProps) => {
  const [showing, setShowing] = useStoreToggle('DEV_TOOLS');
  return (
    <Container showing={showing} translate="no">
      {showing ? (
        <DevToolsPanel
          close={() => setShowing(false)}
          currentState={currentState}
          funnelId={funnelId}
          next={next}
        />
      ) : (
        <Button onClick={() => setShowing(true)}>Demo Tools</Button>
      )}
    </Container>
  );
};

type DevToolsPanelProps = DevToolsButtonProps & {
  close: () => void;
};

export const DevToolsPanel = ({ close, currentState, funnelId, next }: DevToolsPanelProps) => {
  const isSponsoredSolar = funnelId === 'sponsoredSolar';
  const matches = currentState?.matches || (() => false);
  const isEnrollmentState = currentState?.value?.toString().toLowerCase().includes('enrollment');
  const context = currentState?.context;
  const utilityId = context?.utilityId || context?.funnelPacket?.utility?.id || 0;
  const isAmeren = TEST_DATA.utilities.ameren.id === utilityId;
  const isConEd = TEST_DATA.utilities.conEd.id === utilityId;
  const partnerProvided =
    context?.enrollmentOptions?.length && context.enrollmentOptions[0] === 'partner_provided';
  const isPerchEnergy = funnelId === PERCH_FUNNEL_ID;

  const showsAutofillUtilities =
    matches('utility') ||
    matches('createUtilityAccount') ||
    (location.pathname.includes('/community-solar-national') && !store.get('UTILITY_SELECTION'));

  const [noDashboard, setNoDashboard] = useStoreToggle('NO_DASHBOARD');
  const [showDiagrams, setShowDiagrams] = useStoreToggle('SHOW_DIAGRAMS');
  const [usePreEnrollmentParams, setUsePreEnrollmentParams] = useStoreToggle(
    'USE_PRE_ENROLLMENT_PARAMS',
  );
  const [showLocalize, setShowLocalize] = useState(false);

  const clearAndReload = (url?: string, queryParams?: Record<string, string>) => {
    store.clearAll();
    store.set('DEV_TOOLS', true);
    store.set('SHOW_DIAGRAMS', showDiagrams);
    store.set('NO_DASHBOARD', noDashboard);
    store.set('USE_PRE_ENROLLMENT_PARAMS', true);
    const queryString = queryParams ? new URLSearchParams(queryParams).toString() : '';
    if (queryString)
      url = url ? `${url}?${queryString}` : `${window.location.pathname}?${queryString}`;

    if (url) location.assign(url);
    else location.reload();
  };

  const autofillOptions = useMemo(() => {
    if (showsAutofillUtilities) {
      return (
        <AutofillUtilities
          funnelId={funnelId}
          showPerchProductPicker={!!isPerchEnergy && matches('utility')}
        />
      );
    }

    if (
      (matches('credentials') ||
        isEnrollmentState ||
        location.pathname.includes('/utility-connect')) &&
      !isAmeren
    ) {
      return (
        <CredentialsStatus
          isSponsoredSolar={isSponsoredSolar}
          utilityId={utilityId}
          isConEd={isConEd}
          isPerchEnergy={isPerchEnergy}
          next={data => {
            if (isEnrollmentState) {
              currentState?.children?.enrollmentMachine?.send({ type: 'CREDENTIAL_RESULT', data });
            } else {
              next(data);
            }
          }}
        />
      );
    }

    if ((isAmeren && matches('credentials')) || isEnrollmentState) {
      return (
        <>
          <Button
            onClick={() => {
              const code = randomNumber(10);
              const returnUrl = `${location.origin}/authorize/ameren?code=pw-${code}&iss=1&client_id=1`;
              location.assign(returnUrl);
            }}
            mode="primary"
          >
            Ameren OAuth
          </Button>

          <Button mode="primary" onClick={() => autofill({ utilityId })}>
            Autofill
          </Button>
        </>
      );
    }

    if ((matches('credentials') || isEnrollmentState) && !partnerProvided && isAmeren) {
      return (
        <Button
          onClick={() => {
            currentState?.children?.enrollmentMachine?.send({
              type: 'CREDENTIAL_RESULT',
              data: {
                amerenCode: undefined,
                utilityConnectStatus: 'timed_out',
              },
            });
          }}
          mode="primary"
        >
          Utility Connect Timeout
        </Button>
      );
    }

    if (
      matches('billpay') ||
      matches('payment') ||
      matches('billing') ||
      matches('stripePayment')
    ) {
      return (
        <>
          <Button mode="primary" onClick={() => copyToClipboard('4242424242424242')}>
            Copy test credit card
          </Button>
          <Label margin={{ bottom: '16px', top: '16px' }}>Or simulate response:</Label>
          <Button
            mode="outline"
            mb={1}
            onClick={() =>
              next({ type: 'SUBMIT_NEW_SETUP_INTENT', data: { lastFour: '4242', source: 'Visa' } })
            }
            size="small"
          >
            Payment created
          </Button>
        </>
      );
    }

    return (
      <Button mode="primary" onClick={() => autofill({ utilityId })}>
        Autofill
      </Button>
    );
  }, [currentState, funnelId, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = [
    businessConfig,
    communityHousingPartnersConfig,
    communitySolarConfig,
    conferenceConfig,
    constellationConfig,
    goSolarConfig,
    isolarUcbConfig,
    membershipConfig,
    nonABPConfig,
    oregonTier1Config,
    oregonTier2Config,
    solarLandscapeConfig,
    sponsoredSolarConfig,
    telesalesConfig,
    telesalesNoPaymentConfig,
    waitlListConfig,
    powerSetterConfig,
    oregonTier1LmiConfig,
    minnesotaLmiConfig,
    oregonTier2LmiConfig,
    perchEnergyConfig,
    ...[dashboardBillpayUpgradeConfig, resumingConfig, welcomeBackConfig].filter(
      config => config.path === location.pathname,
    ),
  ].map(funnel => ({
    text: funnel.id === 'nonABP' ? 'go-illinois' : kebabCase(funnel.id),
    value: funnel.path,
  }));

  const funnelChangeHandler = (path: string) => {
    const queryParams: Record<string, string> = {};
    if (usePreEnrollmentParams) {
      if (path.includes('isolar')) {
        queryParams.promoCode = 'isolarusa';
      }
    }
    const queryParamKeys = Object.keys(queryParams);
    if (queryParamKeys.length > 0) {
      // eslint-disable-next-line no-console
      console.log('%c Restarting with query params:', 'color: #212529; background-color: #a1cca5;');
      queryParamKeys.forEach(key => {
        // eslint-disable-next-line no-console
        console.log(
          `%c ${key} %c ${queryParams[key]}`,
          'color: #f8f9fa; background-color: #ef476f;',
          'color: #212529; background-color: #fdc500;',
        );
      });
      clearAndReload(path, queryParams);
      return;
    }
    clearAndReload(path);
  };
  return (
    <Controls>
      <Button onClick={close}>Close</Button>
      <Select
        label="Funnel"
        minHeight="auto"
        name="select-funnel"
        options={sortBy(options, ['text'])}
        onChange={funnelChangeHandler}
        value={funnelId === 'sponsoredSolar' ? '/community-solar-national' : location.pathname}
      />
      {autofillOptions}
      {isPerchEnergy ? <PerchEnergyDevInfo context={context} /> : null}

      <Button onClick={() => funnelChangeHandler(window.location.pathname)} mode="destructive">
        Restart
      </Button>
      <Additional>
        <Summary>More...</Summary>
        <Checkbox
          checked={!!usePreEnrollmentParams}
          onChange={ev => {
            const value = ev.target.checked;
            setUsePreEnrollmentParams(value);
          }}
        >
          Use pre-enrollment query params
        </Checkbox>
        <Checkbox
          checked={!!showDiagrams}
          onChange={ev => {
            const value = ev.target.checked;
            setShowDiagrams(value);
            if (value) location.reload();
          }}
        >
          Show diagrams
        </Checkbox>
        <Checkbox
          checked={!!noDashboard}
          onChange={ev => {
            const value = ev.target.checked;
            setNoDashboard(value);
          }}
        >
          No dashboard redirect
        </Checkbox>
        <Checkbox
          checked={showLocalize}
          onChange={({ target: { checked } }) => {
            // eslint-disable-next-line no-unused-expressions
            checked ? window.Localize?.showWidget() : window.Localize?.hideWidget();
            setShowLocalize(checked);
          }}
        >
          Show Localize widget
        </Checkbox>
        <Button mode="primary" onClick={() => copyToClipboard(store.get('TOKEN') || '')}>
          Copy token
        </Button>
        <Button
          mode="primary"
          onClick={() => copyToClipboard(store.get('USER_ID')?.toString() || '')}
        >
          Copy user id
        </Button>
        <ResumingButtons clearAndReload={clearAndReload} currentState={currentState} />
      </Additional>
    </Controls>
  );
};

export const autofill = ({ utilityId }: { utilityId: number }) => {
  setValueByName('email', `freddie.flexington+${randomNumber(6)}@arcadia.com`);
  setValueByName('businessName', "Freddie's Flowers");
  setValueByName('firstName', 'Freddie');
  setValueByName('lastName', 'Flexington');
  setValueByName('password', 'password12345');
  setValueByName('phone', '(866) 526-0083');
  setValueByName('utilityAccountNumber', generateAccountNumber(utilityId));
  setValueByName('stripeCustomerId', `cus_${randomNumber(6)}`);
  setValueByName('stripePaymentId', `pm_${randomNumber(6)}`);
  setValueByName('Date of birth', '02/02/1990');
  setValueByName('jobTitle', 'Product Manager');
  setValueByName('businessPhone', '(888) 526-0084');
  autoFillSecondaryIdentifierField(utilityId);

  let meterNumber;
  if (utilityId === UTILITY_IDS['Portland General Electric']) {
    meterNumber = `${randomNumber(8)}IN`;
  } else if (utilityId === UTILITY_IDS['Dominion Energy VA']) {
    meterNumber = `${randomNumber(10)}`;
  } else if (utilityId === UTILITY_IDS['Eversource / NSTAR']) {
    meterNumber = `${randomNumber(7)}`;
  } else if (utilityId === UTILITY_IDS['Delmarva Power MD']) {
    meterNumber = `TEST${randomNumber(8)}`;
  } else if (utilityId === UTILITY_IDS['Ameren IL']) {
    meterNumber = `${randomNumber(8)}`;
  } else if (utilityId === UTILITY_IDS['Commonwealth Edison']) {
    meterNumber = `${randomNumber(9)}`;
  } else {
    meterNumber = `TEST${randomNumber(6)}`;
  }
  setValueByName('meterNumber', meterNumber);
  setValueByName('accountHolderName', 'Freddie Flexington');
  setValueByName(
    'address',
    utilityData.find(utility => utility.id === utilityId.toString())?.address ||
      '234 W 43rd St, New York, NY 10036, USA',
  );
  setValueByName('addressSecondary', `${randomNumber(3)}`);
  setValueByName('estimatedAnnualKwh', `${randomNumber(4)}`);
  setValueByName('estimatedDailyKwh', '10');

  const submitButton = document.querySelector('[type=submit]') as HTMLButtonElement;
  if (submitButton) {
    submitButton.scrollIntoView();
    submitButton.focus();
  }
};

export const autoFillSecondaryIdentifierField = (utilityId: number) => {
  const liveUtility = store.get('UTILITY_SELECTION');
  const utility =
    liveUtility?.id === utilityId
      ? liveUtility
      : Object.values(TEST_DATA.utilities).find(u => u.id === utilityId)!;

  const secondaryIdRules = utility?.fieldValidationRules?.filter(
    rule => rule.fieldType === 'secondary_account_identifier',
  );

  if (secondaryIdRules && secondaryIdRules.length !== 0) {
    let generatedSecondaryIdentifier = false;

    for (const rule of secondaryIdRules) {
      const pattern = rule.patterns?.[0];
      if (pattern) {
        try {
          setValueByName('secondaryIdentifier', new RandExp(pattern).gen());
          generatedSecondaryIdentifier = true;
          break;
        } catch {
          // Some backend patterns are valid for validation but unsupported by randexp.
        }
      }
    }

    if (!generatedSecondaryIdentifier) {
      setValueByName('secondaryIdentifier', `${randomNumber(10)}`);
    }
  }
};

export const sponsoredSolarConfig: FunnelConfig = {
  id: 'sponsoredSolar',
  path: '/community-solar-national',
  show: () => true,
};
